/**
 * PR-H — polar tracking guide line.
 *
 * World-space dashed line that visualises the active polar tracking
 * angle (AutoCAD `POLARANG` sysvar). Visible when `POLARMODE > 0` and
 * the cursor lies near one of the configured angles. ORTHOMODE
 * collapses the angle table to {0°, 90°} for the duration that
 * ortho is active.
 *
 * # Mesh strategy
 *
 * One persistent `THREE.Line` (dashed material). Per-frame the
 * caller updates the two endpoint positions; if no angle locks, the
 * line is hidden (`group.visible = false`).
 *
 * # Angle table
 *
 * `polarAngleTable(polarAngDeg, orthoEnabled, angleBasis, referenceAngleDeg)`
 * produces the angle list:
 *
 *   - ORTHOMODE on   → [0, 90, 180, 270]   (cardinal axes)
 *   - ORTHOMODE off  → multiples of `polarAngDeg` from 0 to 360,
 *                     plus the implicit 0/45/90 markers when the user
 *                     has set a non-cardinal angle increment, rotated by
 *                     `referenceAngleDeg` when `angleBasis === "relative"`
 *                     (N6 POLAR-RELATIVE-BASIS-WIRE).
 *
 * AutoCAD parity: `POLARANG = 30` produces [0, 30, 60, 90, ...]; the
 * 0°/90° angles are *always* present (so dropping ORTHOMODE doesn't
 * lose the cardinal locks).
 */

import * as THREE from "three";

import {
  buildPolarAngleTable,
  nearestPolarAngle,
  DEFAULT_POLAR_THRESHOLD_DEG,
  type PolarAngleBasis,
} from "../snap/polarConstraint";

/**
 * Compute the table of polar tracking angles in *degrees* for the guide overlay.
 * ORTHOMODE collapses the list to {0, 90, 180, 270} (ortho + polar are mutually
 * exclusive, so an ortho-active session shows the cardinal cross only).
 *
 * Delegates to the AUTHORITATIVE {@link buildPolarAngleTable} in
 * `snap/polarConstraint.ts` so the multiples-of-increment logic has a single
 * source of truth shared with the drafting-lock constraint — the overlay never
 * re-derives it. Returns a sorted unique-ascending array.
 *
 * `angleBasis` / `referenceAngleDeg` (N6) default to `"absolute"` / `0` —
 * every pre-N6 2-arg call site (this module's own tests, any other caller
 * that has not opted into relative basis) is byte-identical to before.
 */
export function polarAngleTable(
  polarAngDeg: number,
  orthoEnabled: boolean,
  angleBasis: PolarAngleBasis = "absolute",
  referenceAngleDeg = 0,
): number[] {
  if (orthoEnabled) {
    return [0, 90, 180, 270];
  }
  return buildPolarAngleTable(
    {
      incrementAngleDeg: polarAngDeg,
      additionalAnglesDeg: [],
      useAdditionalAngles: false,
      angleBasis,
    },
    referenceAngleDeg,
  );
}

/**
 * Find the nearest tracking angle to `cursorAngleDeg` within the threshold
 * (default 3°), or `null` when none lock. Thin adapter over the shared
 * {@link nearestPolarAngle} so the overlay and the constraint agree exactly.
 */
export function findActiveAngle(
  cursorAngleDeg: number,
  table: number[],
  thresholdDeg = DEFAULT_POLAR_THRESHOLD_DEG,
): number | null {
  return nearestPolarAngle(cursorAngleDeg, table, thresholdDeg);
}

/**
 * Polar guide handle. The caller owns mounting `line` into the
 * scene; `setEndpoints(from, to)` updates the segment per frame.
 * `setHidden()` hides without unmounting.
 */
export interface PolarGuideHandle {
  line: THREE.Line;
  setEndpoints(
    from: { x: number; y: number },
    to: { x: number; y: number },
  ): void;
  setHidden(): void;
  dispose(): void;
}

const POLAR_GUIDE_COLOR = "#00c864"; // teal-green — matches SVG renderer

/**
 * Build the polar guide line. Empty by default (hidden until the
 * caller pushes endpoints).
 */
export function createPolarGuide(): PolarGuideHandle {
  const geometry = new THREE.BufferGeometry();
  const positions = new Float32Array([0, 0, 0, 0, 0, 0]);
  geometry.setAttribute(
    "position",
    new THREE.BufferAttribute(positions, 3),
  );

  const material = new THREE.LineDashedMaterial({
    color: new THREE.Color(POLAR_GUIDE_COLOR),
    dashSize: 6,
    gapSize: 4,
    transparent: true,
    opacity: 0.55,
    depthTest: false,
    depthWrite: false,
  });

  const line = new THREE.Line(geometry, material);
  line.userData = { kind: "polar-guide" };
  line.visible = false;
  line.renderOrder = 50;
  line.frustumCulled = false;

  const positionAttr = geometry.attributes.position as THREE.BufferAttribute;

  return {
    line,
    setEndpoints(from, to) {
      const arr = positionAttr.array as Float32Array;
      arr[0] = from.x;
      arr[1] = from.y;
      arr[2] = 0;
      arr[3] = to.x;
      arr[4] = to.y;
      arr[5] = 0;
      positionAttr.needsUpdate = true;
      line.computeLineDistances();
      line.visible = true;
    },
    setHidden() {
      line.visible = false;
    },
    dispose() {
      geometry.dispose();
      material.dispose();
    },
  };
}
